/**
 * SettingsPanel - Watch page settings panel
 * Manages Featured Hero Carousel (1-5 rotating slides) and general page settings
 */
import { Star, Save, Loader2, Plus, Trash2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../../ui/button';
import { useState, useEffect } from 'react';
import { ImageUpload } from '../ImageUpload';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MAX_SLIDES = 5;

const emptySlide = () => ({ broadcast_id: '', youtube_url: '', title: '', description: '', thumbnail_url: '' });

export const SettingsPanel = ({
  settings,
  setSettings,
  onSaveSettings,
  saving
}) => {
  const [completedGames, setCompletedGames] = useState([]);
  const [expandedSlide, setExpandedSlide] = useState(null);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API}/broadcasts/schedule`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setCompletedGames(data.filter(g => g.is_completed && (g.youtube_url || g.youtube_vod_url || g.recording_url || g.vcloud_url)));
        }
      } catch (e) { console.error(e); }
    };
    fetchGames();
  }, []);

  const hero = settings.featured_hero || {};
  const heroEnabled = hero.enabled || false;
  const items = hero.items || [];
  const cycleSeconds = hero.auto_cycle_seconds || 8;

  const updateHero = (updates) => {
    setSettings({ ...settings, featured_hero: { ...hero, ...updates } });
  };

  const updateSlide = (idx, updates) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], ...updates };
    updateHero({ items: newItems });
  };

  const addSlide = () => {
    if (items.length >= MAX_SLIDES) return;
    updateHero({ items: [...items, emptySlide()] });
    setExpandedSlide(items.length);
  };

  const removeSlide = (idx) => {
    const newItems = items.filter((_, i) => i !== idx);
    updateHero({ items: newItems });
    if (expandedSlide === idx) setExpandedSlide(null);
    else if (expandedSlide > idx) setExpandedSlide(expandedSlide - 1);
  };

  const moveSlide = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= items.length) return;
    const newItems = [...items];
    [newItems[idx], newItems[newIdx]] = [newItems[newIdx], newItems[idx]];
    updateHero({ items: newItems });
    setExpandedSlide(newIdx);
  };

  const getSlideLabel = (slide, idx) => {
    if (slide.title) return slide.title;
    if (slide.broadcast_id) {
      const game = completedGames.find(g => g.id === slide.broadcast_id);
      if (game) {
        const away = typeof game.away_team === 'object' ? game.away_team?.name : game.away_team;
        const home = typeof game.home_team === 'object' ? game.home_team?.name : game.home_team;
        return `${away} @ ${home}`;
      }
    }
    if (slide.youtube_url) return slide.youtube_url.substring(0, 40) + '...';
    return `Slide ${idx + 1}`;
  };

  return (
    <div className="space-y-6">
      {/* Hero Carousel Settings */}
      <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-4 h-4 text-orange-500" />
          <h3 className="text-white font-bold">Featured Hero Carousel</h3>
          <span className="text-zinc-500 text-xs ml-auto">Up to {MAX_SLIDES} rotating slides</span>
        </div>

        <label className="flex items-center gap-3 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={heroEnabled}
            onChange={(e) => updateHero({ enabled: e.target.checked })}
            className="w-5 h-5 rounded"
            data-testid="hero-enabled-toggle"
          />
          <div>
            <p className="text-white text-sm">Enable Hero Carousel</p>
            <p className="text-zinc-500 text-xs">Rotating featured banners at the top of the Watch page</p>
          </div>
        </label>

        {heroEnabled && (
          <div className="space-y-4 border-t border-white/5 pt-4">
            {/* Cycle Speed */}
            <div className="flex items-center gap-4">
              <label className="text-zinc-400 text-xs uppercase whitespace-nowrap">Auto-cycle speed</label>
              <input
                type="range"
                min={4}
                max={15}
                value={cycleSeconds}
                onChange={(e) => updateHero({ auto_cycle_seconds: parseInt(e.target.value) })}
                className="flex-1 accent-orange-500"
                data-testid="hero-cycle-speed"
              />
              <span className="text-white text-sm font-mono w-8 text-right">{cycleSeconds}s</span>
            </div>

            {/* Slides */}
            <div className="space-y-2">
              {items.map((slide, idx) => (
                <div key={idx} className="bg-zinc-800 rounded-lg border border-white/5 overflow-hidden" data-testid={`hero-slide-${idx}`}>
                  {/* Slide header */}
                  <div className="flex items-center gap-3 px-4 py-3">
                    <GripVertical className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                    <span className="text-orange-500 text-xs font-bold w-5">{idx + 1}</span>
                    <span className="text-white text-sm flex-1 truncate">{getSlideLabel(slide, idx)}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveSlide(idx, -1)} disabled={idx === 0}
                        className="p-1 hover:bg-zinc-700 rounded disabled:opacity-20 transition-colors" title="Move up">
                        <ChevronUp className="w-3 h-3 text-zinc-400" />
                      </button>
                      <button onClick={() => moveSlide(idx, 1)} disabled={idx === items.length - 1}
                        className="p-1 hover:bg-zinc-700 rounded disabled:opacity-20 transition-colors" title="Move down">
                        <ChevronDown className="w-3 h-3 text-zinc-400" />
                      </button>
                      <button onClick={() => setExpandedSlide(expandedSlide === idx ? null : idx)}
                        className="p-1 hover:bg-zinc-700 rounded transition-colors text-zinc-400 hover:text-white" title="Edit">
                        {expandedSlide === idx ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <button onClick={() => removeSlide(idx)}
                        className="p-1 hover:bg-red-500/20 rounded transition-colors" title="Remove">
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </div>

                  {/* Slide edit form (expanded) */}
                  {expandedSlide === idx && (
                    <div className="px-4 pb-4 pt-1 space-y-3 border-t border-white/5">
                      <div>
                        <label className="text-zinc-400 text-xs uppercase mb-1 block">Select a Game</label>
                        <select
                          value={slide.broadcast_id || ''}
                          onChange={(e) => updateSlide(idx, { broadcast_id: e.target.value })}
                          className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm rounded"
                          data-testid={`hero-slide-game-${idx}`}
                        >
                          <option value="">-- No game (use custom URL) --</option>
                          {completedGames.map(g => {
                            const away = typeof g.away_team === 'object' ? g.away_team?.name : g.away_team;
                            const home = typeof g.home_team === 'object' ? g.home_team?.name : g.home_team;
                            return <option key={g.id} value={g.id}>{away} @ {home} — {g.scheduled_date}</option>;
                          })}
                        </select>
                      </div>
                      <div>
                        <label className="text-zinc-400 text-xs uppercase mb-1 block">Or YouTube URL</label>
                        <input
                          type="text"
                          value={slide.youtube_url || ''}
                          onChange={(e) => updateSlide(idx, { youtube_url: e.target.value })}
                          placeholder="https://youtube.com/watch?v=..."
                          className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm rounded"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-zinc-400 text-xs uppercase mb-1 block">Title</label>
                          <input
                            type="text"
                            value={slide.title || ''}
                            onChange={(e) => updateSlide(idx, { title: e.target.value })}
                            placeholder="Slide title"
                            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm rounded"
                          />
                        </div>
                        <div>
                          <label className="text-zinc-400 text-xs uppercase mb-1 block">Description</label>
                          <input
                            type="text"
                            value={slide.description || ''}
                            onChange={(e) => updateSlide(idx, { description: e.target.value })}
                            placeholder="Short description"
                            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm rounded"
                          />
                        </div>
                      </div>
                      <ImageUpload
                        value={slide.thumbnail_url || ''}
                        onChange={(url) => updateSlide(idx, { thumbnail_url: url })}
                        label="Custom Thumbnail"
                        placeholder="Leave blank to auto-detect from YouTube"
                        previewSize="md"
                      />
                    </div>
                  )}
                </div>
              ))}

              {/* Add Slide Button */}
              {items.length < MAX_SLIDES && (
                <button
                  onClick={addSlide}
                  className="w-full py-3 border border-dashed border-white/10 hover:border-orange-500/50 text-zinc-500 hover:text-orange-400 text-sm flex items-center justify-center gap-2 transition-colors rounded-lg"
                  data-testid="add-hero-slide-btn"
                >
                  <Plus className="w-4 h-4" /> Add Slide ({items.length}/{MAX_SLIDES})
                </button>
              )}

              {items.length === 0 && (
                <p className="text-zinc-600 text-xs text-center py-2">Add at least one slide to display the hero carousel.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* General Settings */}
      <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
        <h3 className="text-white font-bold mb-4">General Settings</h3>
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.show_live_indicator !== false}
              onChange={(e) => setSettings({ ...settings, show_live_indicator: e.target.checked })}
              className="w-5 h-5 rounded"
            />
            <div>
              <p className="text-white text-sm">Show Live Indicators</p>
              <p className="text-zinc-500 text-xs">Show "LIVE" badge on currently streaming content</p>
            </div>
          </label>
        </div>
      </div>

      <Button onClick={onSaveSettings} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="save-settings-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        Save Settings
      </Button>
    </div>
  );
};

export default SettingsPanel;
